import React, { useState } from 'react';
import { X, Sparkles, MessageSquare, Send, Copy, Check, Globe, Sliders } from 'lucide-react';
import { generateResponse } from '../../services/aiService';

export default function AIResponseModal({ lead, isOpen, onClose }) {
  const [language, setLanguage] = useState('English');
  const [tone, setTone] = useState('Professional & Persuasive');
  const [channel, setChannel] = useState('whatsapp');
  const [generatedText, setGeneratedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !lead) return null;

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setCopied(false);
    try {
      const res = await generateResponse({
        leadId: lead.id,
        language,
        tone,
        channel,
      });
      if (res.success && res.message) {
        setGeneratedText(res.message);
      } else {
        setError('Failed to generate response.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error generating AI reply');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!generatedText) return;
    navigator.clipboard.writeText(generatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendWhatsApp = () => {
    if (!lead?.number) return;
    const cleanedNumber = lead.number.replace(/[^0-9]/g, '');
    const url = `https://wa.me/${cleanedNumber}?text=${encodeURIComponent(generatedText)}`;
    window.open(url, '_blank');
  };

  const handleSendEmail = () => {
    if (!lead?.email) return;
    const url = `mailto:${lead.email}?subject=${encodeURIComponent(`Platinum Motors - Follow up regarding your inquiry`)}&body=${encodeURIComponent(generatedText)}`;
    window.open(url, '_blank');
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      background: 'rgba(0, 0, 0, 0.65)',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'var(--surface, #1e293b)',
        color: 'var(--text, #f8fafc)',
        borderRadius: '16px',
        maxWidth: '540px',
        width: '100%',
        padding: '20px',
        border: '1px solid var(--border, #334155)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyBetween: 'space-between', borderBottom: '1px solid var(--border, #334155)', paddingBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              padding: '8px',
              borderRadius: '10px',
              background: 'rgba(16, 185, 129, 0.15)',
              color: '#10b981'
            }}>
              <Sparkles size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text)' }}>
                1-Click AI Response Generator
              </h3>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)' }}>
                Multi-language sales reply for {lead.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'var(--muted)',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Options Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '700', color: 'var(--muted)', marginBottom: '4px' }}>
              <Globe size={12} /> Language
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              style={{
                width: '100%',
                fontSize: '12px',
                padding: '6px 8px',
                borderRadius: '6px',
                background: 'var(--surface-2)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                outline: 'none'
              }}
            >
              <option value="English">English</option>
              <option value="Urdu">Urdu (Urdu script)</option>
              <option value="Roman Urdu">Roman Urdu</option>
              <option value="Arabic">Arabic</option>
              <option value="Hindi">Hindi</option>
              <option value="Spanish">Spanish</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '700', color: 'var(--muted)', marginBottom: '4px' }}>
              <Sliders size={12} /> Tone
            </label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              style={{
                width: '100%',
                fontSize: '12px',
                padding: '6px 8px',
                borderRadius: '6px',
                background: 'var(--surface-2)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                outline: 'none'
              }}
            >
              <option value="Professional & Persuasive">Professional</option>
              <option value="Friendly & Warm">Friendly & Warm</option>
              <option value="Formal & Direct">Formal & Direct</option>
              <option value="Urgent Follow-Up">Urgent Follow-Up</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '700', color: 'var(--muted)', marginBottom: '4px' }}>
              <MessageSquare size={12} /> Channel
            </label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              style={{
                width: '100%',
                fontSize: '12px',
                padding: '6px 8px',
                borderRadius: '6px',
                background: 'var(--surface-2)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                outline: 'none'
              }}
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
            </select>
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={loading}
          style={{
            width: '100%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #059669, #0d9488)',
            color: '#ffffff',
            fontWeight: '600',
            fontSize: '13px',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.15s ease'
          }}
        >
          <Sparkles size={15} className={loading ? 'animate-spin' : ''} />
          <span>{loading ? 'Generating Reply...' : generatedText ? 'Regenerate Reply' : 'Generate AI Reply'}</span>
        </button>

        {error && (
          <div style={{
            fontSize: '12px',
            color: '#f87171',
            background: 'rgba(239, 68, 68, 0.1)',
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}>
            {error}
          </div>
        )}

        {/* Response Preview Box */}
        {generatedText && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{
              background: 'var(--surface-2)',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
              <div style={{ display: 'flex', justifyBetween: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#10b981', textTransform: 'uppercase' }}>
                  AI Response Preview
                </span>
                <button
                  onClick={handleCopy}
                  style={{
                    marginLeft: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: 'var(--surface-3)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    cursor: 'pointer'
                  }}
                >
                  {copied ? <Check size={12} style={{ color: '#10b981' }} /> : <Copy size={12} />}
                  <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <textarea
                value={generatedText}
                onChange={(e) => setGeneratedText(e.target.value)}
                rows={5}
                style={{
                  width: '100%',
                  fontSize: '12px',
                  color: 'var(--text)',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  lineHeight: '1.5'
                }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              {channel === 'whatsapp' ? (
                <button
                  onClick={handleSendWhatsApp}
                  disabled={!lead?.number}
                  style={{
                    flex: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: '#059669',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <Send size={14} />
                  <span>Send via WhatsApp</span>
                </button>
              ) : (
                <button
                  onClick={handleSendEmail}
                  disabled={!lead?.email}
                  style={{
                    flex: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: '#2563eb',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <Send size={14} />
                  <span>Send via Email</span>
                </button>
              )}

              <button
                onClick={handleCopy}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: 'var(--surface-2)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {copied ? <Check size={14} style={{ color: '#10b981' }} /> : <Copy size={14} />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
